import React, { useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Button, Modal, Form } from 'react-bootstrap';
import Filter from './components/filter/filter';
import ReportContainer from './components/report-container/report-container';
import { useHistory } from 'react-router-dom';
import ReportsService from '../../../../services/reports-service';
import './checker-reports-module.scss';
import { IdentityContext } from '../../../../context/identity-context';
import PublishReportModal from './components/modal/publish-report-modal/publish-report-modal';
import RejectReportModal from './components/modal/reject-report-modal/reject-report-modal';
import PdfPreviewModal from './components/modal/pdf-preview-modal/pdf-preview-modal';
import FinalPublishSuccessModal from './components/modal/final-publish-success-modal/final-publish-success-modal';

const CheckerReportsModule = () => {
  const reportsService = new ReportsService();
  const { user, role } = useContext(IdentityContext);

  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  //const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [filter, setFilter] = useState({ status: [], paymentDate: '', issuers: [], isins: [] });
  const [comments, setComments] = useState('');
  const [publishSuccessMessage, setPublishSuccessMessage] = useState('');
  const [rejectSuccessMessage, setRejectSuccessMessage] = useState('');
  const [isPublishSuccess, setIsPublishSuccess] = useState(false);
  const [isRejectSuccess, setIsRejectSuccess] = useState(false);
  const [finalPublishSuccessMessage, setFinalPublishSuccessMessage] = useState('');
  const [isFinalPublishSuccess, setIsFinalPublishSuccess] = useState(false);
  const [pdfPreviewUrls, setPdfPreviewUrls] = useState([]); // Array to hold multiple URLs
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0); // Index to manage the current PDF
  const [previewContinue, setPreviewContinue] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const history = useHistory();

  useEffect(() => {
    const getReports = async () => {
      try {
        const result = await reportsService.getReports('checker');
        setItems(result);
        setFilteredItems(result);
        console.log(result);
      } catch (error) {
        console.error('Fetching error:', error);
        history.push('/internal-server-error');
      } finally {
        setLoading(false);
      }
    };
    getReports();
  }, []);

  useEffect(() => {
    const { status = [], paymentDate = '', issuers = [], isins = [] } = filter;
  
    const filtered = items.filter((item) => {
      const matchesStatus = status.length ? status.includes(item.status) : true;
      const matchesPaymentDate = paymentDate ? item.paymentDate === paymentDate : true;
      const matchesIssuer = issuers.length ? issuers.includes(item.issuer) : true;
      const matchesIsin = isins.length ? isins.includes(item.isin) : true;
  
      // Combine all searchable attributes into a single string for matching
      const searchableText = `
        ${item.title} 
        ${item.status} 
        ${item.paymentDate} 
        ${item.issuer} 
        ${item.isin} 
        ${item.description || ''} 
        ${item.otherAttribute || ''}
      `.toLowerCase();
  
      // Check if the search term matches any of the searchable fields
      const matchesSearchTerm = searchableText.includes(searchTerm.toLowerCase());
  
      return matchesStatus && matchesPaymentDate && matchesIssuer && matchesIsin && matchesSearchTerm;
    });
  
    setFilteredItems(filtered);
  }, [filter, items, searchTerm]);
  


  const handleCheckboxChange = (id) => {
    setSelectedReports(prev => {
      const newSelectedReports = new Set(prev);
      if (newSelectedReports.has(id)) {
        newSelectedReports.delete(id);
      } else {
        newSelectedReports.add(id);
      }
      return newSelectedReports;
    });
  };

  const handleReject = () => setShowRejectModal(true);
  const handlePublish = () => setShowPublishModal(true);
  const handleCancel = () => {
    setShowPublishModal(false);
    setShowRejectModal(false);
    setShowPdfPreviewModal(false);
    setComments('');
    setPublishSuccessMessage('');
    setRejectSuccessMessage('');
    setIsPublishSuccess(false);
    setIsRejectSuccess(false);
  };

  const handlePublishContinue = async () => {
    const checkerName = user.attributes['custom:display_name'];
    let allLoaded = true; // Flag to track if all reports are loaded
    try {
      const pdfUrls = [];
      for (const reportId of Array.from(selectedReports)) {
        const report = items.find(item => item.id === reportId);
        if (report) {
          const { fileURL } = await reportsService.retrieveReport(
            report.type,
            report.isin,
            report.title,
            report.paymentDate
          );
          pdfUrls.push({ url: fileURL, title: report.title });
        } else {
          allLoaded = false; // If report is not found, mark as not loaded
        }
      }
      if (allLoaded) {
        setPdfPreviewUrls(pdfUrls);
        setShowPdfPreviewModal(true);
        setShowPublishModal(false);
      } else {
        setPublishSuccessMessage('Some reports failed to load.');
      }
    } catch (error) {
      console.error('Error while fetching report PDFs:', error);
      setPublishSuccessMessage('Some reports failed to load.'); 
      setIsPublishSuccess(false);
    }
  };
  
  const handleFinalPublish = async () => {
    const checkerName = user.attributes['custom:display_name'];
    try {
      for (const reportId of Array.from(selectedReports)) {
        const report = items.find(item => item.id === reportId);
        if (report) {
          await reportsService.checkerSendReport(
            report.type,
            report.title,
            report.isin,
            report.paymentDate,
            checkerName
          );
        }
      }
      setIsFinalPublishSuccess(true); // Set success state
      setFinalPublishSuccessMessage('Reports successfully published!'); // Set success message
      setShowPdfPreviewModal(false);
    } catch (error) {
      console.error('Error while publishing reports:', error);
      setIsFinalPublishSuccess(false); // In case of error, ensure success state is false
      setFinalPublishSuccessMessage('Failed to publish reports.'); // Set error message
      setShowPdfPreviewModal(false);
    }
    setPreviewContinue(false);
  };

  const handleRejectContinue = async () => {
    const checkerName = user.attributes['custom:display_name'];
    try {
      for (const reportId of Array.from(selectedReports)) {
        const report = items.find(item => item.id === reportId);
        if (report) {
          await reportsService.checkerRejectReport(
            report.type,
            report.title,
            report.isin,
            report.paymentDate,
            checkerName,
            comments
          );
        }
      }
      setIsRejectSuccess(true);
      setRejectSuccessMessage('Reports successfully rejected!');
    } catch (error) {
      console.error('Error while rejecting reports:', error);
    }
  };

  const areReportsSelected = selectedReports.size > 0;

  return (
    <Container className="checker-reports-module-container">
      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" />
        </div>
      ) : (
        <Row>
          <Col xs={12} md={2} className="panel">
            <Filter items={items} filter={filter} onFilterChange={setFilter} />
          </Col>
          <Col xs={12} md={10}>
            <Container className="reports-container">
              <Row>
                <Col xs={12}>
                  <div className="search-container">
                    <Form.Control 
                      type="text" 
                      placeholder="Search reports..." 
                      value={searchTerm} 
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className='search-box' 
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <div className="reports-table-container">
                    <ReportContainer filteredReports={filteredItems} handleCheckboxChange={handleCheckboxChange} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <div className="actions-container">
                    <Button
                      onClick={handleReject}
                      className="reject-button"
                      disabled={!areReportsSelected}
                      aria-disabled={!areReportsSelected}
                    >
                      Reject Report
                    </Button>
                    <Button
                      onClick={handlePublish}
                      className="publish-button"
                      variant="primary"
                      disabled={!areReportsSelected}
                      aria-disabled={!areReportsSelected}
                    >
                      Publish Report to Issuer
                    </Button>
                  </div>
                </Col>
              </Row>
            </Container>
            <PublishReportModal
              show={showPublishModal}
              isPublishSuccess={isPublishSuccess}
              publishSuccessMessage={publishSuccessMessage}
              handleCancel={handleCancel}
              handlePublishContinue={handlePublishContinue}
            />

            <RejectReportModal
              show={showRejectModal}
              isRejectSuccess={isRejectSuccess}
              rejectSuccessMessage={rejectSuccessMessage}
              comments={comments}
              setComments={setComments}
              handleCancel={handleCancel}
              handleRejectContinue={handleRejectContinue}
            />

            <PdfPreviewModal
              show={showPdfPreviewModal}
              pdfPreviewUrls={pdfPreviewUrls}
              currentPdfIndex={currentPdfIndex}
              setCurrentPdfIndex={setCurrentPdfIndex}
              handleCancel={handleCancel}
              handleFinalPublish={handleFinalPublish}
            />
          <FinalPublishSuccessModal 
            show={isFinalPublishSuccess}
            onHide={() => setIsFinalPublishSuccess(false)}
            finalPublishSuccessMessage={finalPublishSuccessMessage}
          />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default CheckerReportsModule;
